import { Link } from "react-router-dom";
import {
  Baby,
  CalendarDays,
  ClipboardList,
  FileText,
  LayoutDashboard,
  ListChecks,
  Mail,
  MapPin,
  Package,
  Plus,
  ScanLine,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Tag,
  UserPlus,
  Wallet,
} from "lucide-react";

import { CONTACT_EMAIL, CONTACT_MAILTO } from "../../lib/contact";
import { PLANNED_FEATURE_GROUPS } from "../../lib/plannedFeatures";
import { PublicChrome } from "./PublicChrome";

const BENEFITS = [
  {
    title: "Działa wszędzie",
    Icon: Smartphone,
    text: "W przeglądarce na telefonie, tablecie i komputerze — także na większym terminalu. Możesz dodać AgroHodowcę do ekranu głównego i pracować w oborze bez zasięgu. Kontrola kolczyków, wykoty i wagi, finanse, zwierzęta i zadania zapisują się lokalnie, a po powrocie internetu same synchronizują się z serwerem.",
  },
  {
    title: "Wygodne zarządzanie",
    Icon: ClipboardList,
    text: "Dodajesz zwierzęta po numerze kolczyka, prowadzisz grupy stanówki i sezony wykotów, pulę wolnych kolczyków w Zasobach oraz przychody i wydatki. Na pulpicie widać, co wymaga uwagi — od niedokończonej kontroli stada po zadania z terminem.",
  },
  {
    title: "Bezpieczeństwo danych",
    Icon: ShieldCheck,
    text: "Każde konto ma własne stado — dane nie mieszają się między użytkownikami. Wejście chroni sesja logowania. W ustawieniach możesz włączyć MFA, osobne hasło administratora oraz wyczyścić dane lub usunąć konto z okresem ochronnym.",
  },
] as const;

const STEPS = [
  {
    n: "1",
    title: "Zarejestruj się",
    Icon: UserPlus,
    text: "Zarejestruj się e-mailem (hasło min. 8 znaków, potem link potwierdzający) albo wejdź przez Google. Potem logujesz się na telefonie i komputerze.",
  },
  {
    n: "2",
    title: "Dodaj swoje stado",
    Icon: Plus,
    text: "Wpisz pierwsze sztuki po kolczyku albo wczytaj zestaw przykładowy przyciskiem „Przykłady”, żeby poćwiczyć bez własnych danych.",
  },
  {
    n: "3",
    title: "Zarządzaj hodowlą z dowolnego miejsca",
    Icon: MapPin,
    text: "Pulpit, zwierzęta, rozród (stanówka i wykoty), kontrole, zasoby, finanse, raporty PDF i IRZplus — w jednym menu. W oborze bez zasięgu pracujesz normalnie; zmiany wyślą się, gdy wróci internet.",
  },
] as const;

const CURRENT_MODULES = [
  {
    title: "Pulpit i Szukaj owcy",
    text: "Kafelki stada, sprawy do zrobienia, zadania, finanse miesiąca i szybkie wyszukiwanie po kolczyku.",
  },
  {
    title: "Zwierzęta",
    text: "Rejestr stada, archiwum odejść i porównanie z IRZplus.",
  },
  {
    title: "Rozród",
    text: "Grupy stanówki i wykotów: pokrycia, urodzenia, wagi, matki bez wykotu.",
  },
  {
    title: "Kontrole",
    text: "Weryfikacja kolczyków w terenie (ważenie, szczepienia i odrobaczanie — wkrótce).",
  },
  {
    title: "Zasoby",
    text: "Pula wolnych kolczyków urzędowych do przydziału jagniętom i nowym sztukom.",
  },
  {
    title: "IRZ, Raporty, Finanse",
    text: "Synchronizacja z IRZplus, PDF z kontroli oraz rejestr wydatków i przychodów.",
  },
] as const;

const MOCK_ROWS = [
  { Icon: LayoutDashboard, label: "Pulpit — stan gospodarstwa" },
  { Icon: Tag, label: "Zwierzęta — rejestr stada" },
  { Icon: CalendarDays, label: "Rozród — stanówka i wykoty" },
  { Icon: Package, label: "Zasoby — wolne kolczyki" },
  { Icon: ScanLine, label: "Kontrole — weryfikacja kolczyków" },
  { Icon: Wallet, label: "Finanse tego miesiąca" },
  { Icon: FileText, label: "Raporty PDF i IRZplus" },
] as const;

export function LandingPage() {
  return (
    <PublicChrome>
      <main>
        <section className="mx-auto grid max-w-6xl gap-12 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-2 lg:items-center lg:gap-16 lg:py-20">
          <div>
            <p className="text-sm font-bold uppercase tracking-wide text-brand">AgroHodowca</p>
            <h1 className="mt-3 text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              Twoja hodowla pod pełną kontrolą — na telefonie i komputerze
            </h1>
            <p className="mt-5 max-w-xl text-lg leading-relaxed text-muted">
              Papierowe listy i notatki w telefonie łatwo zgubić. AgroHodowca zbiera rejestr stada owiec,
              stanówki, wykoty, kolczyki, kontrole, finanse i pracę z IRZplus w jednym miejscu.
            </p>
            <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-soft px-4 py-2 text-sm font-semibold text-brand">
              Beta — pełny dostęp za darmo. Płatne plany z co najmniej 30-dniowym wyprzedzeniem.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <Link
                to="/login"
                className="inline-flex min-h-14 touch-manipulation items-center justify-center rounded-xl bg-brand px-7 text-lg font-semibold text-white shadow-sm hover:bg-brand-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                data-testid="landing-hero-login"
              >
                Zaloguj się
              </Link>
              <Link
                to="/register"
                className="inline-flex min-h-14 touch-manipulation items-center justify-center rounded-xl border-2 border-brand bg-white px-7 text-lg font-semibold text-brand hover:bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                data-testid="landing-hero-register"
              >
                Zarejestruj się
              </Link>
            </div>
          </div>

          <div className="rounded-3xl border border-line bg-white p-4 shadow-sm sm:p-6" aria-hidden="true">
            <div className="mb-4 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <LayoutDashboard className="h-5 w-5 text-brand" strokeWidth={2} aria-hidden />
                <span>Podgląd systemu</span>
              </div>
              <span className="inline-flex items-center gap-1 rounded-full bg-soft px-3 py-1 text-xs font-semibold text-brand">
                <Smartphone className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                Telefon i komputer
              </span>
            </div>
            <ul className="grid gap-2">
              {MOCK_ROWS.map(({ Icon, label }) => (
                <li
                  key={label}
                  className="flex min-h-12 items-center gap-3 rounded-2xl border border-line bg-canvas px-3 py-2.5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-soft text-brand">
                    <Icon className="h-5 w-5" strokeWidth={2} aria-hidden />
                  </span>
                  <span className="text-sm font-medium sm:text-base">{label}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div className="rounded-2xl bg-soft px-2 py-3">
                <ListChecks className="mx-auto h-5 w-5 text-brand" strokeWidth={2} aria-hidden />
                <p className="mt-1 text-xs font-semibold">Zadania</p>
              </div>
              <div className="rounded-2xl bg-soft px-2 py-3">
                <Baby className="mx-auto h-5 w-5 text-brand" strokeWidth={2} aria-hidden />
                <p className="mt-1 text-xs font-semibold">Wykoty</p>
              </div>
              <div className="rounded-2xl bg-soft px-2 py-3">
                <ShieldCheck className="mx-auto h-5 w-5 text-brand" strokeWidth={2} aria-hidden />
                <p className="mt-1 text-xs font-semibold">IRZplus</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
            <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">
              Po co to jest?
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-center text-base leading-relaxed text-muted sm:text-lg">
              Proste narzędzia na co dzień przy owcach — bez żargonu i bez ściany przycisków.
            </p>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {BENEFITS.map(({ title, Icon, text }) => (
                <article
                  key={title}
                  className="rounded-3xl border border-line bg-canvas px-6 py-8"
                >
                  <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-brand shadow-sm">
                    <Icon className="h-7 w-7" strokeWidth={2} aria-hidden />
                  </span>
                  <h3 className="mt-5 text-xl font-bold">{title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-muted">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">
            Co jest w aplikacji
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-base leading-relaxed text-muted sm:text-lg">
            Aktualny zakres modułów — zgodny z menu po zalogowaniu.
          </p>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {CURRENT_MODULES.map(({ title, text }) => (
              <article
                key={title}
                className="rounded-3xl border border-line bg-white px-5 py-6"
              >
                <h3 className="text-lg font-bold">{title}</h3>
                <p className="mt-2 text-base leading-relaxed text-muted">{text}</p>
              </article>
            ))}
          </div>
          <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-muted">
            Po zalogowaniu pełną instrukcję znajdziesz w menu →{" "}
            <Link to="/guide" className="font-semibold text-brand hover:underline">
              Przewodnik
            </Link>
            .
          </p>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
            <div className="rounded-3xl border-2 border-brand bg-soft px-6 py-8 sm:px-10 sm:py-10">
              <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Faza beta — korzystaj za darmo
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
                Obecnie udostępniamy pełną funkcjonalność bezpłatnie, żebyś mógł przetestować
                aplikację w swoim gospodarstwie. Planowane płatne plany ogłosimy z co najmniej
                30-dniowym wyprzedzeniem — szczegóły w{" "}
                <Link to="/terms" className="font-semibold text-brand hover:underline">
                  regulaminie
                </Link>
                .
              </p>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
                Konta założone w fazie beta mogą otrzymać preferencyjne warunki po uruchomieniu
                płatności — model benefitu zostanie ogłoszony przed startem płatnych planów.
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <h2 className="text-center text-2xl font-bold tracking-tight sm:text-3xl">Jak to działa</h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-base leading-relaxed text-muted sm:text-lg">
            Trzy kroki. Nie musisz być „od komputerów”.
          </p>
          <ul className="mt-10 grid list-none gap-6 md:grid-cols-3">
            {STEPS.map(({ n, title, Icon, text }) => (
              <li key={n} className="relative rounded-3xl border border-line bg-white px-6 py-8">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand text-white">
                  <Icon className="h-7 w-7" strokeWidth={2} aria-hidden />
                </span>
                <p className="mt-5 text-sm font-bold uppercase tracking-wide text-brand">Krok {n}</p>
                <h3 className="mt-1 text-xl font-bold">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-muted">{text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
            <div className="rounded-3xl border-2 border-brand bg-soft px-6 py-8 sm:px-10 sm:py-10">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-brand shadow-sm">
                <Mail className="h-7 w-7" strokeWidth={2} aria-hidden />
              </span>
              <h2 className="mt-5 text-2xl font-bold tracking-tight sm:text-3xl">
                Kontakt z administratorem
              </h2>
              <p className="mt-3 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
                Masz pytanie o konto, rejestrację albo działanie aplikacji? Napisz — odpowiemy na
                skrzynkę administratora.
              </p>
              <a
                href={CONTACT_MAILTO}
                className="mt-6 inline-flex min-h-14 touch-manipulation items-center justify-center rounded-xl bg-brand px-7 text-lg font-semibold text-white shadow-sm hover:bg-brand-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                data-testid="landing-contact-email"
              >
                {CONTACT_EMAIL}
              </a>
            </div>
          </div>
        </section>

        <section className="bg-white">
          <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
            <div className="mx-auto max-w-3xl text-center">
              <span className="inline-flex items-center gap-2 rounded-full bg-soft px-4 py-1.5 text-sm font-semibold text-brand">
                <Sparkles className="h-4 w-4" strokeWidth={2} aria-hidden />
                Wkrótce
              </span>
              <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">
                Co dodamy w najbliższej przyszłości
              </h2>
              <p className="mt-3 text-base leading-relaxed text-muted sm:text-lg">
                Pracujemy nad integracją z IRZplus, nowymi gatunkami i raportami, które odciążą
                papierową robotę w gospodarstwie.
              </p>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {PLANNED_FEATURE_GROUPS.map(({ title, items }) => (
                <article
                  key={title}
                  className="rounded-3xl border border-line bg-canvas px-6 py-7"
                >
                  <h3 className="text-lg font-bold">{title}</h3>
                  <ul className="mt-4 space-y-2.5 text-base leading-relaxed text-muted">
                    {items.map((item) => (
                      <li key={item} className="flex gap-2.5">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
            <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-muted">
              Po zalogowaniu pełny opis planowanych funkcji znajdziesz w{" "}
              <Link to="/guide#planowane" className="font-semibold text-brand hover:underline">
                Przewodniku
              </Link>
              .
            </p>
          </div>
        </section>
      </main>
    </PublicChrome>
  );
}
